import ApplicationController from "../application_component_controller";
import { formatBytes } from "hb/lib/number_helpers";

// Stream picker interaction model:
// - The list shows only projects the user has added (data-added="true").
//   Every project group is pre-rendered so form submission collects all
//   stream_ids[] values, but unadded groups stay hidden via CSS.
// - A Bootstrap dropdown (data-bs-toggle="dropdown") offers unadded
//   projects. Selecting one flips the group's data-added and auto-checks
//   its unattached streams.
// - Removing a project unchecks its streams and hides the group again.
// - Per-stream checkboxes stay available inside each added group so the
//   user can opt out of either Customer or Internal individually.
export default class extends ApplicationController {
  static targets = [
    "search",
    "searchDropdown",
    "searchResult",
    "searchEmpty",
    "group",
    "groupSummary",
    "streamCheckbox",
    "summaryCount",
    "summarySize",
    "emptyState",
  ];

  connect() {
    this.groupTargets.forEach((g) => this.updateGroupSummary(g));
    this.filterSearchResults();
    this.updateTotals();
  }

  searchInput() {
    this.filterSearchResults();
  }

  searchKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      const first = this.visibleSearchResults()[0];
      if (first) first.querySelector(".dropdown-item")?.click();
    }
  }

  addProject(event) {
    event.preventDefault();
    const li = event.currentTarget.closest(
      "[data-archive-destinations--stream-picker-component-target~='searchResult']",
    );
    const name = li?.dataset.projectName;
    if (name) this.addProjectByName(name);
  }

  removeProject(event) {
    event.preventDefault();
    const group = this.closestGroup(event.currentTarget);
    if (!group) return;

    group.dataset.added = "false";
    this.streamsIn(group).forEach((cb) => {
      if (!cb.disabled) cb.checked = false;
    });

    this.updateGroupSummary(group);
    this.filterSearchResults();
    this.updateTotals();
  }

  streamChanged(event) {
    const group = this.closestGroup(event.target);
    if (group) this.updateGroupSummary(group);
    this.updateTotals();
  }

  addProjectByName(name) {
    const group = this.groupTargets.find((g) => g.dataset.projectName === name);
    if (!group) return;

    group.dataset.added = "true";
    group.parentElement.prepend(group);
    this.streamsIn(group).forEach((cb) => {
      if (!cb.disabled) cb.checked = true;
    });

    this.searchTarget.value = "";
    this.updateGroupSummary(group);
    this.filterSearchResults();
    this.updateTotals();
  }

  streamsIn(groupEl) {
    return Array.from(groupEl.querySelectorAll('input[name="stream_ids[]"]'));
  }

  closestGroup(el) {
    return el.closest(
      "[data-archive-destinations--stream-picker-component-target~='group']",
    );
  }

  updateGroupSummary(groupEl) {
    const boxes = this.streamsIn(groupEl).filter((cb) => !cb.disabled);
    const total = boxes.length;
    const selected = boxes.filter((cb) => cb.checked).length;
    const el = groupEl.querySelector(
      "[data-archive-destinations--stream-picker-component-target~='groupSummary']",
    );
    if (el) el.textContent = `${selected}/${total} selected`;
  }

  updateTotals() {
    const checked = this.streamCheckboxTargets.filter((cb) => cb.checked);
    const bytes = checked.reduce(
      (acc, cb) => acc + (parseInt(cb.dataset.bytes, 10) || 0),
      0,
    );
    if (this.hasSummaryCountTarget)
      this.summaryCountTarget.textContent = String(checked.length);
    if (this.hasSummarySizeTarget)
      this.summarySizeTarget.textContent = formatBytes(bytes);

    const anyAdded = this.groupTargets.some((g) => g.dataset.added === "true");
    if (this.hasEmptyStateTarget)
      this.emptyStateTarget.classList.toggle("d-none", anyAdded);
  }

  filterSearchResults() {
    const query = this.searchTarget.value.trim().toLowerCase();
    const addedNames = new Set(
      this.groupTargets
        .filter((g) => g.dataset.added === "true")
        .map((g) => g.dataset.projectName),
    );
    let visible = 0;
    this.searchResultTargets.forEach((r) => {
      const name = r.dataset.projectName || "";
      const taken = addedNames.has(name);
      const matches = query === "" || name.toLowerCase().includes(query);
      const show = !taken && matches;
      r.classList.toggle("d-none", !show);
      if (show) visible++;
    });
    if (this.hasSearchEmptyTarget)
      this.searchEmptyTarget.classList.toggle("d-none", visible > 0);
  }

  visibleSearchResults() {
    return this.searchResultTargets.filter(
      (r) => !r.classList.contains("d-none"),
    );
  }
}
